import { ReactNode } from 'react';
import { primaryProductImageUrl } from '@/lib/productImage';
import { RAGStatus } from '../machines/RAGBadge';
import { SlotCard } from './SlotCard';

export interface SlotGridSlot {
  id: string;
  slotNumber: number;
  slotLabel: string | null;
  productId: string | null;
  productName?: string;
  product?: {
    id: string;
    name: string;
    sku: string;
    imageUrl?: string | null;
    media?: Array<{ url: string; mediaType: string }>;
    isActive?: boolean | null;
    status?: string | null;
    deletedAt?: string | Date | null;
  } | null;
  currentQuantity: number;
  maxCapacity: number;
  ragStatus: { status: RAGStatus; percentageFilled: number };
  /** Replaces the quantity line on the card (e.g. planogram price + margin). */
  footer?: ReactNode;
}

interface SlotGridProps {
  slots: SlotGridSlot[];
  /** Used only for slots without a label: how many appear per row (chunking). */
  columns?: number;
  onSlotClick?: (slot: SlotGridSlot) => void;
}

function chunkIntoRows<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

/** First character of the slot label (row on the machine). Empty if no label. */
function rowKeyFromLabel(slotLabel: string | null): string {
  const t = (slotLabel || '').trim();
  return t.length > 0 ? t[0].toUpperCase() : '';
}

function sortSlotsInRow(a: SlotGridSlot, b: SlotGridSlot): number {
  const la = (a.slotLabel || '').trim();
  const lb = (b.slotLabel || '').trim();
  if (la && lb) {
    const c = la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
    if (c !== 0) return c;
  }
  return a.slotNumber - b.slotNumber;
}

/** Order physical rows: A–Z, then 0–9, then other single-character keys, then fallback chunks last. */
function compareRowKeys(a: string, b: string): number {
  const rank = (key: string): number => {
    if (key.startsWith('__FALLBACK_')) return 300 + parseInt(key.slice(11), 10);
    if (key.length !== 1) return 200;
    const c = key.charCodeAt(0);
    if (c >= 65 && c <= 90) return c - 65;
    if (c >= 48 && c <= 57) return 100 + (c - 48);
    return 200;
  };
  const d = rank(a) - rank(b);
  return d !== 0 ? d : a.localeCompare(b);
}

type LayoutRow = { displayLabel: string; slots: SlotGridSlot[] };

function buildLayoutRows(slots: SlotGridSlot[], columnsFallback: number): LayoutRow[] {
  const labeled: SlotGridSlot[] = [];
  const unlabeled: SlotGridSlot[] = [];

  for (const s of slots) {
    if (rowKeyFromLabel(s.slotLabel)) {
      labeled.push(s);
    } else {
      unlabeled.push(s);
    }
  }

  const byRowKey = new Map<string, SlotGridSlot[]>();
  for (const s of labeled) {
    const key = rowKeyFromLabel(s.slotLabel);
    const list = byRowKey.get(key);
    if (list) {
      list.push(s);
    } else {
      byRowKey.set(key, [s]);
    }
  }

  for (const list of Array.from(byRowKey.values())) {
    list.sort(sortSlotsInRow);
  }

  const labelRows: LayoutRow[] = Array.from(byRowKey.entries())
    .sort(([ka], [kb]) => compareRowKeys(ka, kb))
    .map(([key, rowSlots]) => ({ displayLabel: key, slots: rowSlots }));

  unlabeled.sort((a, b) => a.slotNumber - b.slotNumber);
  const fallbackChunks = chunkIntoRows(unlabeled, columnsFallback);
  const hasLabeled = labelRows.length > 0;
  const fallbackRows: LayoutRow[] = fallbackChunks.map((rowSlots, i) => ({
    displayLabel: hasLabeled ? `R${i + 1}` : String.fromCharCode(65 + i),
    slots: rowSlots,
  }));

  return [...labelRows, ...fallbackRows];
}

export function SlotGrid({ slots, columns = 5, onSlotClick }: SlotGridProps) {
  const layoutRows = buildLayoutRows(slots, columns);

  return (
    <div className="space-y-4">
      {layoutRows.map((row, rowIndex) => (
        <div key={rowIndex} className="flex gap-3 items-stretch">
          <div
            className="w-10 shrink-0 self-start pt-4 text-center text-sm font-semibold text-muted-foreground tabular-nums"
            aria-hidden
          >
            {row.displayLabel}
          </div>
          <div className="min-w-0 flex-1 overflow-x-auto">
            <div
              className="grid min-w-min gap-3"
              style={{
                gridTemplateColumns: `repeat(${row.slots.length}, minmax(17rem, max-content))`,
              }}
            >
              {row.slots.map((slot) => (
                <SlotCard
                  key={slot.id}
                  slotNumber={slot.slotNumber}
                  slotLabel={slot.slotLabel}
                  productId={slot.productId}
                  productName={slot.productName}
                  productImageUrl={primaryProductImageUrl(slot.product ?? null)}
                  currentQuantity={slot.currentQuantity}
                  maxCapacity={slot.maxCapacity}
                  ragStatus={slot.ragStatus.status}
                  footer={slot.footer}
                  onClick={() => onSlotClick?.(slot)}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default SlotGrid;
